import React, { useEffect, useState, useRef, useCallback } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Modal, Button } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Spinner from "../components/Common/Spinner";
import {
  createNewAdmin,
  deleteAdmin,
  getFavVendor,
  updateAdminInfo,
  updateAdminStatus,
  getCategory,
  addFile,
  getUrl,
  getSubAdmins,
  vendorCategory,
} from "../../services/Admin/adminServices";
import debounce from "lodash/debounce";
import Pagination from "../components/Pagination";
import { useLocation, useParams } from "react-router-dom";

const CategoryVendors = () => {
  const { id } = useParams();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("");
  const [categories, setCategories] = useState([]);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // 🌟 Image & Upload States
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const category = queryParams.get("category");
  const [submit, setSubmit] = useState(false);

  // 🌟 State for the Confirmation Modal
  const [confirmState, setConfirmState] = useState({
    show: false,
    action: null,
    id: null,
    status: null,
    message: "",
  });

  // 🌟 State for the Change Category Modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedVendorForCategory, setSelectedVendorForCategory] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [updatingCategory, setUpdatingCategory] = useState(false);

  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  const fetchCategories = async () => {
    try {
      const res = await getCategory(0, 100);
      setCategories(res?.data?.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getSubAdmins(
        currentPage,
        itemsPerPage,
        "Vendor",
        category,
        debouncedSearch,
        startDate,
        endDate
      );
      setAdmins(response?.data?.data?.data || []);

      setTotalItems(response?.data?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load vendor");
    } finally {
      setLoading(false);
    }
  }, [
    id,
    currentPage,
    itemsPerPage,
    debouncedSearch,
    startDate,
    endDate,
    type,
    category,
  ]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    handler();
    return () => handler.cancel();
  }, [search]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, startDate, endDate, type]);

  const getUrlImage = async (img) => {
    try {
      return await getUrl(img);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileUpload = async (e, fieldName, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const res = await addFile(formData);

      if (res) {
        setPreview(URL.createObjectURL(file));
        setFieldValue("image", res?.data?.data?.fileUrl);
      }
    } catch (error) {
      toast.error("File upload failed");
      setPreview("");
    } finally {
      setUploading(false);
    }
  };

  const openModal = async (admin = null) => {
    setSelectedAdmin(admin);
    setShowModal(true);

    const vendorData = admin?.vendor || admin;

    if (vendorData?.image) {
      try {
        setImageLoading(true);
        const res = await getUrlImage(vendorData.image);
        setPreview(res?.data?.data);
      } catch (error) {
        console.error(error);
      } finally {
        setImageLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (deleteId) => {
    try {
      await deleteAdmin(deleteId);
      toast.success("User deleted");
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete user");
      console.error(error);
    }
  };

  const handlestatus = async (statusId) => {
    try {
      const payload = { userId: statusId };
      await updateAdminStatus(payload);
      toast.success("Status updated successfully");
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
      console.error(error);
    }
  };

  const AdminSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setSubmit(true);
      if (selectedAdmin) {
        const payload = {
          id: selectedAdmin?._id,
          name: values.name,
          image: values.image,
        };
        await updateAdminInfo(payload);
        toast.success("Vendor updated successfully");
      } else {
        await createNewAdmin(values);
        toast.success("Vendor added successfully");
        resetForm();
      }
      fetchAdmins();
      handleCloseModal();
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setSubmit(false);
    }
  };

  const handleClear = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
  };

  // 🌟 Trigger Confirmation Modal
  const triggerConfirm = (action, id, status = null) => {
    let message = "";
    if (action === "delete") {
      message =
        "Are you sure you want to delete this vendor? This action cannot be undone.";
    } else if (action === "status") {
      message = `Are you sure you want to ${status.toLowerCase()} this vendor?`;
    }

    setConfirmState({ show: true, action, id, status, message });
  };

  // 🌟 Execute Action After Confirmation
  const confirmActionHandler = () => {
    const { action, id } = confirmState;
    if (action === "delete") {
      handleDelete(id);
    } else if (action === "status") {
      handlestatus(id);
    }
    // Close modal
    setConfirmState({
      show: false,
      action: null,
      id: null,
      status: null,
      message: "",
    });
  };

  // 🌟 Open Category Modal
  const openCategoryModal = (admin) => {
    setSelectedVendorForCategory(admin);
    // Pre-select: Try businessDetails first, then fallback to the category from the URL param
    const currentCategoryId = admin?.businessDetails?.category?._id || admin?.businessDetails?.category || category || "";
    setNewCategory(typeof currentCategoryId === 'object' ? currentCategoryId._id : currentCategoryId);
    setShowCategoryModal(true);
  };

  // 🌟 Close Category Modal
  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setSelectedVendorForCategory(null);
    setNewCategory("");
  };

  // 🌟 Handle Category Submit
  const handleCategorySubmit = async () => {
    if (!newCategory) {
      toast.error("Please select a category");
      return;
    }
    try {
      setUpdatingCategory(true);
       const payload ={
        id:selectedVendorForCategory._id,
        categoryId: newCategory

       }
     await vendorCategory(payload)
      
      toast.success("Category updated successfully");
      fetchAdmins(); // Refresh the table data
      closeCategoryModal();
    } catch (error) {
      toast.error("Failed to update category");
      console.error(error);
    } finally {
      setUpdatingCategory(false);
    }
  };

  const isFilterActive = search || startDate || endDate;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 style={{ color: "#64CAE5" }}>Vendors</h2>
      </div>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-center mb-4">
        <div className="flex-grow-1">
          <label
            className="form-label mb-1"
            style={{ fontSize: "12px" }}
          ></label>
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            className="form-control w-100"
            placeholder="Search by name, email....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>
            Start Date
          </label>
          <input
            type="date"
            title="Start Date"
            style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            className="form-control w-100"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>
            End Date
          </label>
          <input
            type="date"
            title="End Date"
            style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            className="form-control w-100"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {isFilterActive && (
          <div className="d-flex flex-column">
            <label className="form-label mb-1" style={{ fontSize: "12px" }}>
              &nbsp;
            </label>
            <button
              className="btn btn-outline-danger"
              onClick={handleClear}
              style={{ borderRadius: "0.5rem" }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <table className="table table-striped align-middle shadow-sm">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone Number</th>
              <th>Joined Date</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {admins?.length > 0 ? (
              admins.map((admin, index) => (
                <tr key={admin._id || index}>
                  <td>{currentPage * itemsPerPage + index + 1}</td>
                  <td>
                    {admin?.name
                      ? `${admin.name.charAt(0).toUpperCase()}${admin.name.slice(1)}`
                      : ""}
                  </td>
                  <td>{admin?.email}</td>
                  <td>
                    {admin?.phoneNumber
                      ? `${admin.countryCode || ""} ${admin.phoneNumber}`
                      : "_"}
                  </td>
                  <td>
                    {admin?.createdAt && (
                      <>
                        {new Date(admin.createdAt).toLocaleDateString("en-CA", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                        {", "}
                        {new Date(admin.createdAt).toLocaleTimeString("en-CA", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        !admin?.isBlocked ? "bg-success" : "bg-danger"
                      }`}
                    >
                      {!admin?.isBlocked ? "Active" : "Suspended"}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="light"
                        id={`dropdown-basic-${index}`}
                        style={{ padding: "4px 8px" }}
                      >
                        {svg1}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => openModal(admin)}>
                          Edit
                        </Dropdown.Item>

                        {/* 🌟 New Change Category Action */}
                        <Dropdown.Item onClick={() => openCategoryModal(admin)}>
                          Change Category
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() =>
                            triggerConfirm(
                              "status",
                              admin?._id,
                              admin?.isBlocked ? "Enable" : "Suspend"
                            )
                          }
                        >
                          {admin?.isBlocked ? "Enable" : "Suspend"}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => triggerConfirm("delete", admin._id)}
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">
                  No vendors found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div className="card-footer clearfix">
        <div className="d-flex justify-content-between align-items-center">
          <div className="dataTables_info" style={{ color: "#64CAE5" }}></div>
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <Pagination
              pageCount={totalPages}
              pageValue={currentPage}
              setPage={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedAdmin ? "Edit Vendor" : "Add Vendor"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            name: selectedAdmin?.vendor?.name || selectedAdmin?.name || "",
            image: selectedAdmin?.vendor?.image || selectedAdmin?.image || "",
          }}
          validationSchema={AdminSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue }) => (
            <FormikForm>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <Field
                    name="name"
                    className={`form-control ${
                      errors.name && touched.name ? "is-invalid" : ""
                    }`}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={(e) =>
                      handleFileUpload(e, "image", setFieldValue)
                    }
                  />
                  <ErrorMessage
                    name="image"
                    component="div"
                    className="text-danger mt-1"
                  />
                </div>

                {uploading ? (
                  <div className="text-success mt-2">Uploading image...</div>
                ) : imageLoading ? (
                  <div>Loading image preview...</div>
                ) : (
                  preview && (
                    <div className="mb-3 text-center mt-3">
                      <div
                        style={{
                          display: "inline-block",
                          position: "relative",
                        }}
                      >
                        <img
                          src={preview}
                          alt="preview"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm shadow"
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: "1",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                          onClick={() => {
                            setPreview("");
                            if (fileInputRef.current)
                              fileInputRef.current.value = "";
                            setFieldValue("image", "");
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Close
                </Button>
                <Button
                  type="submit"
                  disabled={uploading}
                  style={{ backgroundColor: "#64CAE5", border: "none" }}
                >
                  {submit ? "Saving" : "Save Changes"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* 🌟 New Confirmation Modal */}
      <Modal
        show={confirmState.show}
        onHide={() => setConfirmState({ ...confirmState, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 fs-5">{confirmState.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmState({ ...confirmState, show: false })}
          >
            Cancel
          </Button>
          <Button
            variant={confirmState.action === "delete" ? "danger" : "primary"}
            style={
              confirmState.action !== "delete"
                ? {
                    backgroundColor: "#64CAE5",
                    borderColor: "#64CAE5",
                    color: "white",
                  }
                : {}
            }
            onClick={confirmActionHandler}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🌟 New Change Category Modal */}
      <Modal 
        show={showCategoryModal} 
        onHide={closeCategoryModal} 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Select a Category</label>
            <select
              className="form-select"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            >
              <option value="">-- Choose Category --</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCategoryModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: "#64CAE5", borderColor: "#64CAE5" }}
            onClick={handleCategorySubmit}
            disabled={updatingCategory}
          >
            {updatingCategory ? "Saving..." : "Save Category"}
          </Button>
        </Modal.Footer>
      </Modal>
      
    </div>
  );
};

export default CategoryVendors;